import './programpopup.less'

import {Icon} from '@codeurs/front'
import {BreakoutroomModel} from 'models/program/BreakoutroomModel'
import {ProgramModel, ProgramNavigationItem} from 'models/program/ProgramModel'
import {SequenceModel} from 'models/program/SequenceModel'
import {SequenceSessionModel} from 'models/program/SequenceSessionModel'
import {SessionModel} from 'models/program/SessionModel'
import {SpeakerModel} from 'models/program/SpeakerModel'
import React, {Fragment, useEffect, useState} from 'react'
import {AnchorAttrs, SummitType, WysiwygType} from 'types'
import useRouter from 'use-react-router'
import {style} from 'util/style'
import {PopupModal} from 'views/layout/popupmodal'
import {BreakoutPopup} from 'views/program/popups/breakoutpopup'
import {SequencePopup} from 'views/program/popups/sequencepopup'
import {SessionPopup} from 'views/program/popups/sessionpopup'
import {SpeakerPopup} from 'views/program/popups/speakerpopup'
import {closeProgramItemPopup, ProgramLink} from 'views/program/programlink'
import {Theme} from 'views/theme'

const styles = style('programpopup', [
	'header',
	'speakers',
	'title',
	'text',
	'parent',
	'links'
])

export type ProgramPopupType = {
	program: ProgramModel
	item?: ProgramNavigationItem
}

export const ProgramPopup: React.FC<ProgramPopupType> = ({program, item}) => {
	const [activeItem, setActiveItem] = useState(item)
	const router = useRouter()
	useEffect(() => {
		if (item && item !== activeItem) setActiveItem(item)
	}, [item])

	return (
		<PopupModal
			open={!!item}
			onClose={() => closeProgramItemPopup(router, activeItem)}
		>
			<ProgramPopupContent item={activeItem} />
		</PopupModal>
	)
}

export const ProgramPopupContent: React.FC<{item: ProgramNavigationItem}> = ({
	item
}) => {
	if (!item) return null

	if (item instanceof SpeakerModel) return <SpeakerPopup speaker={item} />
	if (item instanceof BreakoutroomModel) return <BreakoutPopup room={item} />
	if (item instanceof SessionModel || item instanceof SequenceSessionModel) {
		return <SessionPopup session={item} />
	}
	if (item instanceof SequenceModel) return <SequencePopup sequence={item} />
}

export const ProgramPopupHeader: React.FC<{
	title: string
	start_time?: string
	end_time?: string
	location?: string
}> = ({title, start_time, end_time, location}) => {
	return (
		<div className={styles.header()}>
			<Theme.H5 className={styles.header('title')}>{title}</Theme.H5>
			{start_time && end_time && (
				<Fragment>
					<span className={styles.header('divider')}>/</span>
					<p className={styles.header('time')}>
						{start_time + ` - ` + end_time}
					</p>
				</Fragment>
			)}
			{location && (
				<Fragment>
					<span className={styles.header('divider')}>/</span>
					<p className={styles.header('location')}>{location}</p>
				</Fragment>
			)}
		</div>
	)
}

export const PropgramPopupTitle: React.FC<{
	title?: string
}> = ({title}) => {
	if (!title) return null
	return <h4 className={styles.title()}>{title}</h4>
}

export const ProgramPopupText: React.FC<{
	text: WysiwygType
}> = ({text}) => {
	if (!text) return null

	return (
		<div className={styles.text()}>
			<Theme.Wysiwyg>{text}</Theme.Wysiwyg>
		</div>
	)
}

export const ProgramPopupSpeakers: React.FC<{
	speakers: Array<SpeakerModel>
	active: SpeakerModel
	setSpeaker: (speaker: SpeakerModel) => void
}> = ({speakers, active, setSpeaker}) => {
	if (!speakers || speakers.length === 0) return null

	return (
		<div className={styles.speakers()}>
			<p className={styles.speakers('title')}>Speaker(s): </p>
			{speakers.map((speaker, i) => (
				<Fragment>
					{i > 0 && <span className={styles.speakers('divider')}>/</span>}
					<a
						className={styles.speakers('item').is({active: active === speaker})}
						onClick={() => setSpeaker(speaker)}
					>
						{speaker.name}
					</a>
				</Fragment>
			))}
		</div>
	)
}

export const ProgramPopupParent: React.FC<{
	parent: SequenceModel | BreakoutroomModel
	summit?: SummitType
} & AnchorAttrs> = ({parent, summit, ...rest}) => {
	return (
		<ProgramLink className={styles.parent().mod(summit)} item={parent}>
			<div className={styles.parent('icon')}>
				<Icon icon="arrow-left" />
			</div>
			<div className={styles.parent('title')}>
				This talk is part of the detailed session: {parent.title}
			</div>
		</ProgramLink>
	)
}

export const ProgramPopupLinks: React.FC<{
	sessions: Array<SessionModel | SequenceSessionModel>
}> = ({sessions}) => {
	if (!sessions || sessions.length === 0) return null

	return (
		<div className={styles.links()}>
			{sessions.map(session => (
				<ProgramLink
					item={session}
					key={session.path}
					className={styles.links('item')}
				>
					<div className={styles.links('item-content')}>
						<div className={styles.links('item-content-title')}>
							<Theme.H5>{session.title}</Theme.H5>
						</div>
						{session.description && (
							<div className={styles.links('item-content-text')}>
								<Theme.Wysiwyg>{session.description}</Theme.Wysiwyg>
							</div>
						)}
					</div>
					<div className={styles.links('item-icon')}>
						<Icon icon="arrow-right" />
					</div>
				</ProgramLink>
			))}
		</div>
	)
}
